import React, { useState, useEffect } from 'react';
import styles from './styles.module.css';
import { Form, Input } from 'antd';
import { useNavigate } from 'react-router-dom';
import { useFujiAuth } from '../../../auth/useFujiAuth';
import { useTranslation } from 'react-i18next';
import Header from '../../../components/header/Header';
import CenterLoading from '../../../components/CenterLoading/CenterLoading';
import { isEmptyObject, showNotificationDialog } from '../../../utils';
import ButtonNetwork from '../../../utils/network-connect/ButtonNetwork';
import ConfirmCodeDescription from '../../../components/confirm-code-desscription/ConfirmCodeDescription';
import moment from 'moment';
import { IS_IN_PURCHASE, localStorageKeys } from '../../../utils/Constants';

/**
 * 図101-3, 図102-2 確認コード入力画面
 */
let timeRequestCheck: NodeJS.Timeout;
const timeDelay = 15 * 1000;

const RegisterEmailConfirmScreen = () => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const [seconds, setSeconds] = useState(0);
    const [isLoading, setIsLoading] = useState(false);
    const { tempEmail, resendConfirmationCode, confirmSignUp } = useFujiAuth();

    useEffect(() => {
        if (!tempEmail) {
            navigate('/auth');
        }
    });
    /**
     * 確認ボタンを押下の処理
     *
     * @param {String} code
     */
    const onFinish = ({ code }: { code: string }) => {
        const timeStamp = new Date();
        //ローディングを表示
        setIsLoading(true);
        timeRequestCheck = setTimeout(() => {
            setIsLoading(false);
            showNotificationDialog('error', '', t('notification.title'), t('error-message.server-error'));
        }, timeDelay);

        confirmSignUp(
            code,
            ({
                isSuccessed,
                error,
                onSignIn,
            }: {
                isSuccessed: boolean;
                error: { code: string; message: string[] };
                onSignIn: () => void;
            }) => {
                if (differentTime(timeStamp, new Date()) <= timeDelay) {
                    clearTimeout(timeRequestCheck);
                    //ローディングを非表示
                    setIsLoading(false);
                    if (isSuccessed) {
                        //確認コード完了のメッセージを表示
                        showNotificationDialog(
                            'success',
                            'login_successfully',
                            t('notification.title'),
                            t('notification.login-success'),
                        );
                        // ログインに成功して、画面遷移を行う
                        const isInPurchase = localStorage.getItem(IS_IN_PURCHASE) !== null;
                        const loginFlow = JSON.parse(localStorage.getItem(localStorageKeys.LOGIN_FLOW) ?? '{}');

                        if (isInPurchase || !isEmptyObject(loginFlow)) {
                            // 購入中もしくは購入済み一覧からログインした場合は戻る。
                            navigate(loginFlow.path, { state: loginFlow.state });
                        } else {
                            navigate('/profile');
                        }
                    } else {
                        if (onSignIn != null) {
                            // 1-E6 ログインに失敗
                            showNotificationDialog(
                                'error',
                                'confirm-code-error',
                                t('notification.title'),
                                t('error-message.login.failed'),
                            );
                        } else if (error.code === 'CodeMismatchException') {
                            // 1-E4 2-E6 確認コードが正しくない
                            showNotificationDialog(
                                'error',
                                'confirm-code-error',
                                t('notification.title'),
                                t('error-message.register.incorrect-code'),
                            );
                        } else if (error.message.includes('Network error')) {
                            showNotificationDialog(
                                'error',
                                '',
                                t('notification.title'),
                                t('error-message.server-error'),
                            );
                        } else {
                            // 1-E5 ユーザ登録に失敗
                            showNotificationDialog(
                                'error',
                                'confirm-code-error',
                                t('notification.title'),
                                t('error-message.register.register-failed'),
                            );
                        }
                    }
                }
            },
        );
    };

    const onFinishFailed = (error: any) => {
        console.log('error ', error);
    };

    //時間を比較する
    const differentTime = (now: Date, then: Date) => {
        const ms = moment(now).diff(moment(then));
        return ms;
    };

    /**
     * 確認コードを再送信の実行
     */
    const resendConfirmCode = () => {
        //ローディングを表示
        setIsLoading(true);
        resendConfirmationCode(
            tempEmail,
            ({ isSuccessed, error }: { isSuccessed: boolean; error: { code: string; message: string[] } }) => {
                //ローディングを非表示
                setIsLoading(false);
                if (isSuccessed) {
                    // 確認コード再送信完了のメッセージを表示
                    showNotificationDialog(
                        'success',
                        'resend-confirm-code',
                        t('notification.title'),
                        t('notification.resend-confirm-code'),
                    );
                    startResendCountDown();
                } else if (error.code === 'LimitExceededException') {
                    // 1-E2 2-E4 確認コードのメールの送信上限に達している。
                    showNotificationDialog(
                        'error',
                        'resend-confirm-code',
                        t('notification.title'),
                        t('error-message.login.send-code-limit'),
                    );
                } else if (error.message.includes('Network error')) {
                    showNotificationDialog('error', '', t('notification.title'), t('error-message.server-error'));
                } else {
                    // 1-E3 2-E5 確認コードのメール送信に失敗
                    showNotificationDialog(
                        'error',
                        'resend-confirm-code',
                        t('notification.title'),
                        t('error-message.register.send-code-failed'),
                    );
                }
            },
        );
    };

    /**
     * 確認コード再送信のカウントダウン開始
     */
    const startResendCountDown = () => {
        if (seconds === 0) {
            setSeconds(60);
            const timeInterval = setInterval(() => {
                setSeconds((second) => {
                    const number = second - 1;
                    if (number === 0) {
                        clearInterval(timeInterval);
                    }
                    return number;
                });
            }, 1000);
        }
    };

    return (
        <React.Fragment>
            {isLoading && <CenterLoading overFooter={true} />}
            <Header title={t('header.confirm-code')} />
            <div className={`content-footer-container scrollable ${styles.confirmCodeContent}`}>
                <ConfirmCodeDescription email={tempEmail} />
                <button
                    className={styles.btnResendCode}
                    style={{ opacity: seconds > 0 ? 0.6 : 1 }}
                    disabled={seconds > 0 ? true : false}
                    onClick={resendConfirmCode}
                >
                    {t('confirm-code.resend-code')}
                    {seconds > 0 && <span>({seconds})</span>}
                </button>
                <Form
                    className={styles.confirmForm}
                    initialValues={{
                        remember: true,
                    }}
                    onFinish={onFinish}
                    onFinishFailed={onFinishFailed}
                >
                    <Form.Item
                        name="code"
                        rules={[
                            {
                                required: true,
                                message: t('validate.correct-code') ?? '',
                            },
                        ]}
                    >
                        <Input
                            style={{ borderRadius: 4, height: 50 }}
                            placeholder={t('form.confirm-code') ?? ''}
                            size="large"
                            maxLength={6}
                        />
                    </Form.Item>
                    <Form.Item>
                        <ButtonNetwork title={t('button.login')} type="submit" />
                    </Form.Item>
                </Form>
            </div>
        </React.Fragment>
    );
};

export default RegisterEmailConfirmScreen;
